import {
  useInfiniteQuery,
  type InfiniteData,
  type UseInfiniteQueryOptions,
} from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import type { MessageItem, MessagesPage } from '../types';
import type { ApiResponse } from '@nohara-dxdev/oidc-core';

export const getMessagesQueryKey = (sessionId: string) => ['chat', 'messages', sessionId];

export const listMessages = async (
  orgId: string,
  projectId: string,
  sessionId: string,
  params?: { cursor?: string; limit?: number },
): Promise<MessagesPage> => {
  const { data } = await api.get<
    undefined,
    ApiResponse<{
      data: MessageItem[];
      cursor: string | null;
    }>
  >(`/orgs/${orgId}/projects/${projectId}/ai-agent/sessions/${sessionId}/messages`, { params });

  return {
    messages: data.data,
    cursor: data.cursor ?? undefined,
  };
};

export const getMessageInfiniteQueryOptions = (
  orgId: string,
  projectId: string,
  sessionId: string,
): UseInfiniteQueryOptions<MessagesPage, Error, InfiniteData<MessagesPage>> => {
  return {
    queryKey: getMessagesQueryKey(sessionId),
    queryFn: ({ pageParam }) =>
      listMessages(orgId, projectId, sessionId, {
        cursor: pageParam as string | undefined,
      }),
    getNextPageParam: (lastPage: MessagesPage) => lastPage.cursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled: !!sessionId,
  };
};

type UseMessagesInfiniteOptions = {
  queryConfig?: Partial<UseInfiniteQueryOptions<MessagesPage, Error, InfiniteData<MessagesPage>>>;
} & { orgId: string; projectId: string; sessionId?: string };

export const useMessagesInfinite = ({
  queryConfig,
  orgId,
  projectId,
  sessionId,
}: UseMessagesInfiniteOptions) => {
  return useInfiniteQuery({
    ...getMessageInfiniteQueryOptions(orgId, projectId, sessionId ?? ''),
    ...queryConfig,
  });
};
